import { useAuthStore } from '@/stores/authStore';
import { useSnackbarStore } from '@/stores/snackbarStore';
import { tokenService } from '@/utils/tokenService';
import axios, { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';


// 에러 타입 정의
interface ApiError {
  status?: number;
  message?: string;
  detail?: string;
  code?: string;
  errors?: Record<string, string[]>;
  error?: {
    code?: string;
    message?: string;
  };
}

interface ApiResponse<T = unknown> {
  data: T;
  _meta: {
    status: number;
    statusText: string;
    headers: Record<string, string>;
  };
}

interface TokenRefreshResponse {
  access: string;
  refresh?: string;
}

interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
  skipErrorSnackbar?: boolean;
}

interface RefreshSubscriber {
  originalRequest: ExtendedAxiosRequestConfig;
  resolve: (response: AxiosResponse) => void;
  reject: (error: AxiosError | Error) => void;
}

class ApiClient {
  private instance: AxiosInstance;
  private isRefreshing = false;
  private refreshSubscribers: RefreshSubscriber[] = [];

  // status code별 에러 메시지 매핑
  private errorMessages: Record<number, string> = {
    400: 'Wrong request.',
    401: 'Authentication is required.',
    403: 'Not authorized to access this resource.',
    404: 'Resource not found.',
    409: 'Conflict occurred with the current state of the resource.',
    422: 'Validation error occurred.',
    429: 'Too many requests. Please try again later.',
    500: 'There was a problem with the server. Please try again later.',
    502: 'Bad gateway. Please try again later.',
    503: 'Service unavailable. Please try again later.',
  };

  constructor() {
    this.instance = axios.create({
      baseURL: import.meta.env.VITE_API_URL || '',
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor: Attach access token to all requests
    this.instance.interceptors.request.use(
      (config) => {
        const accessToken = tokenService.getAccessToken();
        if (accessToken) {
          config.headers.Authorization = `Bearer ${accessToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor: Handle responses and errors
    this.instance.interceptors.response.use(
      (response) => this.handleResponse(response),
      async (error: AxiosError) => this.handleError(error)
    );
  }

  private handleResponse(response: AxiosResponse): AxiosResponse {
    // 배열 응답은 그대로 반환
    if (Array.isArray(response.data)) {
      return response;
    }

    // 이미 _meta가 있으면 그대로 반환
    if (response.data?._meta) {
      return response;
    }

    // 응답에 메타데이터 추가
    const originalData = response.data;
    response.data = {
      ...originalData,
      _meta: {
        status: response.status,
        statusText: response.statusText,
        headers: response.headers as Record<string, string>
      }
    };

    return response;
  }

  private async handleError(error: AxiosError): Promise<AxiosResponse<unknown>> {
    const { showSnackbar } = useSnackbarStore.getState();

    const originalRequest = error.config as ExtendedAxiosRequestConfig;
    const skipSnackbar = originalRequest.skipErrorSnackbar;

    // Handle network errors (no response from server)
    if (!error.response) {
      if (!skipSnackbar) {
        showSnackbar('네트워크 연결을 확인해주세요.', { showCloseIcon: true });
      }
      return Promise.reject(this.createApiError(0, '네트워크 오류'));
    }

    const { status, data } = error.response;

    // Handle 401 Unauthorized - Attempt token refresh
    if (status === 401 && !originalRequest._retry) {
      return this.handle401Error(originalRequest);
    }

    // Handle other error status codes
    const errorData = data as ApiError;
    const errorMessage = this.getErrorMessage(status, errorData);

    // Skip snackbar display if requested
    if (skipSnackbar) {
      return Promise.reject(this.createApiError(
        status,
        errorMessage,
        errorData?.detail,
        errorData?.error?.code || errorData?.code,
        errorData?.errors
      ));
    }

    switch (status) {
      case 400:
      case 422:
        // 유효성 검사 에러
        if (errorData?.errors) {
          const firstError = Object.values(errorData.errors)[0];
          if (Array.isArray(firstError) && firstError.length > 0) {
            showSnackbar(firstError[0], { showCloseIcon: true });
          } else {
            showSnackbar(errorMessage, { showCloseIcon: true });
          }
        } else {
          showSnackbar(errorMessage, { showCloseIcon: true });
        }
        break;

      case 403:
        showSnackbar(errorMessage, { showCloseIcon: true });
        break;

      case 404:
        // 404는 개별 처리가 필요할 수 있으므로 에러만 전파
        break;

      case 409:
        showSnackbar(errorMessage, { showCloseIcon: true });
        break;

      case 429:
        showSnackbar(errorMessage, { showCloseIcon: true });
        break;

      case 500:
      case 502:
      case 503:
        showSnackbar('There was a problem with the server. Please try again later.', {
          showCloseIcon: true 
        });
        break;

      default:
        showSnackbar(errorMessage, { showCloseIcon: true });
    }

    // 에러 객체 생성 및 반환
    const apiError = this.createApiError(
      status,
      errorMessage,
      errorData?.detail,
      errorData?.error?.code || errorData?.code,
      errorData?.errors
    );

    return Promise.reject(apiError);
  }

  /**
   * Handle 401 Unauthorized errors
   * Attempts to refresh the access token and retry the original request
   * Uses a queue system to prevent multiple simultaneous refresh attempts
   */
  private async handle401Error(originalRequest: ExtendedAxiosRequestConfig): Promise<AxiosResponse> {
    // Prevent infinite retry loops
    if (originalRequest._retry) {
      this.handleAuthError();
      return Promise.reject(this.createApiError(401, '인증이 만료되었습니다.'));
    }

    originalRequest._retry = true;

    // If already refreshing, queue this request
    if (this.isRefreshing) {
      return new Promise<AxiosResponse>((resolve, reject) => {
        this.refreshSubscribers.push({
          originalRequest,
          resolve,
          reject
        });
      });
    }

    this.isRefreshing = true;

    try {
      // Attempt to refresh the access token
      const newAccessToken = await this.refreshToken();

      // Process all queued requests with the new token
      await this.processQueue(null, newAccessToken);

      // Retry the original request with new token
      if (originalRequest.headers) {
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
      }

      return await this.instance(originalRequest);
    } catch (refreshError) {
      // If refresh fails, reject all queued requests
      await this.processQueue(refreshError as Error);

      this.handleAuthError();
      return Promise.reject(refreshError);
    } finally {
      this.isRefreshing = false;
    }
  }

  /**
   * Process queued requests after token refresh
   * Either resolves with new token or rejects with error
   */
  private async processQueue(error: Error | null = null, token?: string): Promise<void> {
    const subscribers = [...this.refreshSubscribers];
    this.refreshSubscribers = [];

    for (const { originalRequest, resolve, reject } of subscribers) {
      if (error) {
        // Reject all queued requests if refresh failed
        reject(error);
      } else if (token && originalRequest.headers) {
        // Retry each queued request with new token
        originalRequest.headers.Authorization = `Bearer ${token}`;
        try {
          const response = await this.instance(originalRequest);
          resolve(response);
        } catch (err) {
          reject(err as Error);
        }
      }
    }
  }

  private getErrorMessage(status: number, data: ApiError): string {
    // 서버에서 제공한 메시지 우선 사용
    if (data?.message) return data.message;
    if (data?.error?.message) return data.error.message;
    if (data?.detail) return data.detail;

    // 기본 메시지 사용
    return this.errorMessages[status] || '알 수 없는 오류가 발생했습니다.';
  }

  private createApiError(
    status: number,
    message: string,
    detail?: string,
    code?: string,
    errors?: Record<string, string[]>
  ): ApiError {
    return {
      status,
      message,
      detail,
      code,
      errors
    };
  }

  /**
   * Refresh the access token using the refresh token
   * Updates both access and refresh tokens in storage
   * @throws Error if refresh token is missing or refresh fails
   */
  private async refreshToken(): Promise<string> {
    const refreshToken = tokenService.getRefreshToken();

    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      const { data } = await this.instance.post<TokenRefreshResponse>('/auth/token/refresh/', {
        refresh: refreshToken
      });

      // Update access token
      tokenService.setAccessToken(data.access);

      // Update refresh token if provided (token rotation)
      if (data.refresh) {
        tokenService.setRefreshToken(data.refresh);
      }

      return data.access;
    } catch (error) {
      // Clear tokens on refresh failure
      tokenService.removeTokens();
      throw error;
    }
  }

  /**
   * Handle authentication errors
   * Clears all auth state and redirects to home page
   */
  private handleAuthError(): void {
    const { clearUser } = useAuthStore.getState();

    // Clear all authentication tokens
    tokenService.removeTokens();

    // Clear application state
    clearUser();

    // Redirect to home page (this will also clear all TanStack Query caches)
    window.location.href = '/';
  }

  public getInstance() {
    return this.instance;
  }
}

// 싱글톤 인스턴스 생성 및 내보내기
const apiClient = new ApiClient();
export default apiClient.getInstance();

// 타입 내보내기
export type { ApiError, ApiResponse };
